import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  alpha,
  Stack,
  Fade,
  InputAdornment,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export const HeroSection = () => {
  const theme = useTheme();
  const [formData, setFormData] = React.useState({
    nome: "",
    telefone: "",
    email: "",
    tipoPlano: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Dados do formulário:", formData);
    // Lógica para enviar os dados
  };

  const highlights = [
    "Ampla rede credenciada",
    "Atendimento humanizado",
    "Planos com melhor custo-benefício",
    "Descontos em farmácias",
  ];

  return (
    <Box
      sx={{
        pt: { xs: 8, md: 12 },
        pb: { xs: 10, md: 14 },
        backgroundColor: alpha(theme.palette.primary.light, 0.05),
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Coluna de Texto */}
          <Grid item xs={12} md={6}>
            <Fade in={true} timeout={1000}>
              <Box>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2.8rem", sm: "3.5rem", md: "4rem" },
                    lineHeight: 1.2,
                    mb: 3,
                    color: theme.palette.text.primary,
                  }}
                >
                  Sua saúde protegida com os melhores planos
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    mb: 4,
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  Compare e encontre o plano de saúde ideal para você, sua
                  família ou empresa. Consultoria especializada e gratuita.
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {highlights.map((text, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <CheckCircleIcon
                        sx={{
                          color: theme.palette.secondary.main,
                          mr: 1.5,
                          fontSize: 20,
                        }}
                      />
                      <Typography variant="body1">{text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Grid>

          {/* Coluna do Formulário */}
          <Grid item xs={12} md={6} id="formulario">
            <Fade in={true} timeout={1200}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  background: theme.palette.background.paper,
                  boxShadow: `0 10px 30px ${alpha("#9E9E9E", 0.15)}`,
                }}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: theme.palette.primary.main,
                    textAlign: "center",
                  }}
                >
                  Solicite sua Cotação
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                  }}
                >
                  Preencha e receba as melhores ofertas em minutos!
                </Typography>

                <Box component="form" noValidate onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Nome"
                    variant="outlined"
                    margin="normal"
                    required
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon
                            sx={{ mr: 1, color: theme.palette.action.active }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Telefone (WhatsApp)"
                    variant="outlined"
                    margin="normal"
                    required
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    type="tel"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIphoneIcon
                            sx={{ mr: 1, color: theme.palette.action.active }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="E-mail"
                    variant="outlined"
                    margin="normal"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    type="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AlternateEmailIcon
                            sx={{ mr: 1, color: theme.palette.action.active }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth margin="normal" sx={{ mb: 3 }}>
                    <InputLabel id="tipo-plano-label">Tipo de plano</InputLabel>
                    <Select
                      labelId="tipo-plano-label"
                      id="tipo-plano"
                      name="tipoPlano"
                      value={formData.tipoPlano}
                      label="Tipo de plano"
                      onChange={(e) =>
                        handleChange(e as React.ChangeEvent<HTMLInputElement>)
                      }
                      startAdornment={
                        <InputAdornment position="start">
                          <MedicalInformationIcon
                            sx={{
                              mr: 1,
                              color: theme.palette.action.active,
                            }}
                          />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="individual">Individual</MenuItem>
                      <MenuItem value="familiar">Familiar</MenuItem>
                      <MenuItem value="empresarial">Empresarial</MenuItem>
                      <MenuItem value="odontologico">Odontológico</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    type="submit"
                    color="primary"
                    endIcon={<SendIcon />}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      mt: 1,
                    }}
                  >
                    Receber Cotação Gratuita
                  </Button>
                </Box>
              </Paper>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
